import { cn } from "@devhop/ui";
import { ArrowRightIcon } from "lucide-react";
import type {
  EventFeaturesDTO,
  EventProfitLossCostBreakdownDTO,
  EventProfitLossDTO,
} from "../../domain/contracts";
import { eventHasFeature } from "../../domain/services/event-features";
import {
  pickProfitSnapshot,
  type ProfitDisplayMode,
  type ProfitLossView,
} from "../lib/event-profit-loss-ui";
import {
  formatCurrencyBucketsInline,
  formatPerformanceMoney,
} from "./PerformanceReportsNav";

type EventProfitLossBreakdownProps = {
  profitLoss: EventProfitLossDTO;
  costBreakdown: EventProfitLossCostBreakdownDTO;
  view: ProfitLossView;
  displayMode: ProfitDisplayMode;
  eventFeatures: EventFeaturesDTO;
  canReadAssignments: boolean;
  canReadPayouts: boolean;
  outstandingArLak: number;
};

function BreakdownRow({
  label,
  amount,
  hint,
  emphasis,
}: {
  label: string;
  amount: number;
  hint?: string;
  emphasis?: boolean;
}) {
  if (amount === 0 && !emphasis) return null;
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <div className="min-w-0">
        <p className={emphasis ? "font-semibold text-sm" : "text-sm"}>{label}</p>
        {hint ? (
          <p className="mt-0.5 text-muted-foreground text-xs">{hint}</p>
        ) : null}
      </div>
      <p
        className={cn(
          "shrink-0 tabular-nums",
          emphasis ? "font-bold text-base" : "font-medium text-sm",
        )}
      >
        {formatPerformanceMoney(amount, "LAK")}
      </p>
    </div>
  );
}

export function EventProfitLossBreakdown({
  profitLoss,
  costBreakdown,
  view,
  displayMode,
  eventFeatures,
  canReadAssignments,
  canReadPayouts,
  outstandingArLak,
}: EventProfitLossBreakdownProps) {
  const hasPerformers = eventHasFeature(eventFeatures, "performers");
  const hasStaff = eventHasFeature(eventFeatures, "staff");
  const hasProductionCosts = eventHasFeature(eventFeatures, "production_costs");
  const hasOutsources = eventHasFeature(eventFeatures, "outsources");
  const hasCommissions = eventHasFeature(eventFeatures, "commissions");

  const snapshot = pickProfitSnapshot(profitLoss, view, displayMode);
  const converted = profitLoss.converted;
  const revenueView =
    converted &&
    (view === "realized"
      ? converted.revenueCollected
      : converted.revenueProjected);
  const showConvertedRevenue =
    displayMode === "converted" &&
    revenueView &&
    revenueView.lakEquivalent != null;

  const snapshotCosts =
    view === "realized"
      ? costBreakdown.totalPaid
      : costBreakdown.totalCommitted;

  const showPerformer =
    hasPerformers && (canReadAssignments || canReadPayouts);
  const showStaff = hasStaff && canReadPayouts;
  const showProduction = hasProductionCosts;
  const showOutsource = hasOutsources;
  const showCommission = hasCommissions && canReadPayouts;

  const performerAmount =
    view === "realized"
      ? costBreakdown.performerPayoutsPaid
      : costBreakdown.performerCommitted;

  const staffAmount =
    view === "realized"
      ? costBreakdown.staffPayoutsPaid
      : costBreakdown.staffCommitted;

  const productionAmount =
    view === "realized"
      ? costBreakdown.productionPaid
      : costBreakdown.productionCommitted;

  const outsourceAmount =
    view === "realized"
      ? costBreakdown.outsourcePaid
      : costBreakdown.outsourceCommitted;

  const commissionAmount =
    view === "realized"
      ? costBreakdown.commissionPaid
      : costBreakdown.commissionCommitted;

  return (
    <section className="overflow-hidden rounded-xl border bg-card">
      <div className="border-b px-5 py-4">
        <p className="font-semibold text-muted-foreground text-xs uppercase tracking-widest">
          ສູດຄິດ
        </p>
        <p className="mt-1 text-muted-foreground text-xs">
          {view === "realized"
            ? "ລາຍຮັບ − ຕົ້ນທຸນທີ່ຈ່າຍແລ້ວ"
            : "ລາຍຮັບຄາດ − ຕົ້ນທຸນທີ່ commit"}
        </p>
      </div>

      <div className="divide-y px-5">
        {showConvertedRevenue && revenueView ? (
          <div className="py-3">
            <p className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
              ລາຍຮັບ
            </p>
            <div className="flex items-start justify-between gap-4 py-2.5">
              <div className="min-w-0">
                <p className="text-sm">ຕົ້ນສະກຸນ</p>
                <p className="mt-0.5 text-muted-foreground text-xs">
                  {formatCurrencyBucketsInline(revenueView.native)}
                </p>
              </div>
              <ArrowRightIcon className="mt-1 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 text-right">
                <p className="font-semibold text-sm">LAK ເທົ່າຄ່າ</p>
                <p className="mt-0.5 font-medium text-sm tabular-nums">
                  {formatPerformanceMoney(revenueView.lakEquivalent!, "LAK")}
                </p>
              </div>
            </div>
            <div className="flex items-start justify-between gap-4 py-2.5">
              <p className="font-semibold text-sm">ກຳໄລ/ຂາດທຶນ</p>
              <p className="font-bold text-base tabular-nums">
                {snapshot.profit == null
                  ? "—"
                  : formatPerformanceMoney(snapshot.profit, "LAK")}
              </p>
            </div>
            {snapshot.profit == null ? (
              <p className="pb-2 text-muted-foreground text-xs">
                ຍັງຄິດບໍ່ໄດ້ — ອັດຕາບໍ່ຄົບ
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="py-3">
          <p className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
            ຕົ້ນທຸນ
          </p>
          {showPerformer ? (
            <BreakdownRow
              label="ນັກສະແດງ"
              amount={performerAmount}
              hint={
                view === "projected" &&
                costBreakdown.performerCommitted >
                  costBreakdown.performerPayoutsPaid +
                    costBreakdown.performerPayoutsOutstanding
                  ? `ຄ່າຈ້າງທີມ ${formatPerformanceMoney(costBreakdown.performerAssignmentsGross, "LAK")}`
                  : undefined
              }
            />
          ) : null}
          {showStaff ? (
            <BreakdownRow label="Staff" amount={staffAmount} />
          ) : null}
          {showProduction ? (
            <BreakdownRow
              label="Prop / ວັດສະດຸ"
              amount={productionAmount}
              hint={
                view === "projected" && costBreakdown.productionPlanned > 0
                  ? `ວາງແຜນ ${formatPerformanceMoney(costBreakdown.productionPlanned, "LAK")}`
                  : undefined
              }
            />
          ) : null}
          {showOutsource ? (
            <BreakdownRow
              label="Outsource (KOL/Media)"
              amount={outsourceAmount}
              hint={
                view === "projected" && costBreakdown.outsourcePlanned > 0
                  ? `ວາງແຜນ ${formatPerformanceMoney(costBreakdown.outsourcePlanned, "LAK")}`
                  : undefined
              }
            />
          ) : null}
          {showCommission ? (
            <BreakdownRow
              label="ຄ່ານາຍໜ້າ"
              amount={commissionAmount}
            />
          ) : null}
          <BreakdownRow
            label="ລວມຕົ້ນທຸນ"
            amount={snapshotCosts}
            emphasis
          />
        </div>

        {outstandingArLak > 0 ? (
          <div className="py-3">
            <p className="text-muted-foreground text-sm">
              AR ຄ້າງ{" "}
              <span className="font-semibold text-foreground tabular-nums">
                {formatPerformanceMoney(outstandingArLak, "LAK")}
              </span>{" "}
              — ລາຍຮັບຄາດອາດປ່ຽນເມື່ອເກັບຄົບ
            </p>
          </div>
        ) : null}
      </div>
    </section>
  );
}
